import React, { createContext, useContext, useState, useEffect } from 'react';
import { Voucher } from '../utils/api';

interface VoucherContextType {
  appliedVoucher: Voucher | null;
  applyVoucher: (voucher: Voucher) => void;
  removeVoucher: () => void;
}

const VoucherContext = createContext<VoucherContextType | undefined>(undefined);

export const VoucherProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [appliedVoucher, setAppliedVoucher] = useState<Voucher | null>(null);

  useEffect(() => {
    const saved = sessionStorage.getItem('applied_voucher');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Sanity check: if the voucher ID is a legacy mock ID (e.g., 'v1', 'v5'), clear it.
        // Real IDs are UUIDs (36 chars with hyphens).
        if (parsed.id && (parsed.id.length < 20 || !parsed.id.includes('-'))) {
          console.warn('Legacy mock voucher detected, clearing session storage.');
          sessionStorage.removeItem('applied_voucher');
          setAppliedVoucher(null);
        } else {
          setAppliedVoucher(parsed);
        }
      } catch (e) {
        console.error('Failed to parse saved voucher', e);
      }
    }
  }, []);

  const applyVoucher = (voucher: Voucher) => {
    setAppliedVoucher(voucher);
    sessionStorage.setItem('applied_voucher', JSON.stringify(voucher));
  };

  const removeVoucher = () => {
    setAppliedVoucher(null);
    sessionStorage.removeItem('applied_voucher');
  };

  return (
    <VoucherContext.Provider value={{ appliedVoucher, applyVoucher, removeVoucher }}>
      {children}
    </VoucherContext.Provider>
  );
};

export const useVouchers = () => {
  const context = useContext(VoucherContext);
  if (context === undefined) {
    throw new Error('useVouchers must be used within a VoucherProvider');
  }
  return context;
};
